import {
  CommonActions,
  NavigationContainer,
  NavigationContainerRef,
  useNavigation,
} from '@react-navigation/native';
import React, { useEffect, useRef } from 'react';
import { NotificationList } from '../component/Notification/NotificationList';
import { GlobalInterval } from '../hooks/interval/GlobalInterval';
import {
  SubNavigatorName,
  useInitialScreen,
} from '../hooks/login/useInitialScreen';
import IdleScreen from '../screens/IdleScreen/IdleScreen';
import LoginScreen from '../screens/LoginScreen/LoginScreen';
import { CartNavigator } from './CartNavigator';
import AssignPos from '../screens/DeviceSelection/DeviceSelection';
import { sessionSubject } from '../state/sessionObservable';
import { skip } from 'rxjs';
import { useWindowDimensions } from 'react-native';
import { ModalProvider } from '@oolio-group/rn-use-modal';
import Backdrop from '../component/Modals/Backdrop';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useDevices } from '../hooks/organization/useDevices';
import { useStoreSync } from '../hooks/organization/useStoreSync';
import useBehaviorSubjectState from '../hooks/rxjs/useSubjectState';
import { useAppVersionSync } from '../hooks/useAppVersionSync';

const Stack = createNativeStackNavigator();

export const MainNavigator: React.FC = () => {
  const { screen } = useInitialScreen();
  const navigation = useNavigation();
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const { refetch: refetchDevices } = useDevices();
  useStoreSync();

  useEffect(() => {
    sessionSubject.pipe(skip(1)).subscribe(session => {
      if (!session?.authorized) {
        navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: SubNavigatorName.Login }],
          }),
        );
      }
    });
  }, [navigation]);

  // Fetch devices when app loads and user has a store selected
  useEffect(() => {
    if (session?.authorized && session?.currentStore?.id) {
      refetchDevices();
    }
  }, [session?.authorized, session?.currentStore?.id, refetchDevices]);

  useAppVersionSync();

  return screen === SubNavigatorName.Idle ? (
    <IdleScreen />
  ) : (
    <Stack.Navigator
      initialRouteName={screen}
      screenOptions={{ headerShown: false }}
    >
      <Stack.Screen component={LoginScreen} name={SubNavigatorName.Login} />
      <Stack.Screen component={AssignPos} name={SubNavigatorName.AssignPos} />
      <Stack.Screen component={CartNavigator} name={SubNavigatorName.Cart} />
    </Stack.Navigator>
  );
};

const AppNavigator = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigationRef = useRef<NavigationContainerRef<any>>(null);
  // Use useWindowDimensions hook for reactive window size updates
  const { height, width } = useWindowDimensions();

  return (
    <NavigationContainer ref={navigationRef}>
      <ModalProvider
        modalProps={{
          deviceHeight: height,
          deviceWidth: width,
          customBackdrop: <Backdrop />,
          animationInTiming: 50,
          animationOutTiming: 50,
          animationIn: 'fadeIn',
          animationOut: 'fadeOut',
          useNativeDriver: true,
          hideModalContentWhileAnimating: true,
        }}
      >
        <GlobalInterval />
        <NotificationList />
        <MainNavigator />
      </ModalProvider>
    </NavigationContainer>
  );
};

export default AppNavigator;
